import { Flex, GapSize } from "@/components/atoms/flex";
import {
	ActionItemProps,
	ContactItemData,
	MultiselectSearchProps,
	OutboundType,
} from "./types";
import { useStyle } from "./style";
import { MultiInput } from "../multiInput";
import { Avatar, mergeClasses } from "@fluentui/react-components";
import { ListItemProps } from "@/components/atoms/list";
import { SuggestionsPopover } from "../suggestionsPopover";
import { useEffect, useRef, useState } from "react";
import { useDebounce } from "@/common/helpers/useDebounce";
import { captureError } from "@/common/helpers/sentryHelper";
import { TargetSelectionSource } from "@/repository/models/outboundCall";
import { useResponsiveBreakpoints } from "@/hooks/responsive";

export const Multiselect = (props: MultiselectSearchProps) => {
	const classes = useStyle();
	const {
		label,
		value,
		placeholder,
		items,
		actions,
		classesName,
		limit,
		autoFocus,
		isKeyDownHandled,
		showClearButton,
		onInputChanged,
		onItemSelected,
		onInputPaste,
		onInputClear,
		queryFn,
		disabled,
		disableSearchOnMobile = false,
		destinations,
	} = props;
	const inputRef = useRef<HTMLInputElement>(null);
	const [inputValue, setInputValue] = useState<string>("");
	const [listItems, setListItems] = useState<ListItemProps[]>([]);
	const [isPopoverOpen, setPopoverOpen] = useState<boolean>(false);

	const { isMobile } = useResponsiveBreakpoints();

	const hasSuggestions = listItems.length > 0;

	useEffect(
		() => setPopoverOpen(hasSuggestions && !!inputValue),
		[hasSuggestions, inputValue]
	);

	useEffect(() => {
		if (value === undefined) {
			return;
		}

		setInputValue((prev: string) => (value === prev ? prev : value));
	}, [value]);

	const [selectItemCallback] = useDebounce((item: ContactItemData) => {
		onItemSelected(item);
		setListItems([]);
	}, 150);

	const [executeQuery] = useDebounce((searchValue: string) => {
		if (!searchValue) {
			return;
		}

		queryFn(searchValue)
			.then((x) =>
				setListItems(
					x.map((a) => ({
						key: a.key,
						header: a.header,
						secondaryHeader: a.secondaryHeader,
						content: a.content,
						media: <Avatar name={a.data.name} />,
						onClick: () => selectItemCallback(a.data),
					}))
				)
			)
			.catch((err: Error) => {
				captureError(err);
				setListItems([]);
			});
	}, 500);

	useEffect(() => {
		if (!!onInputChanged) {
			onInputChanged(inputValue);
		}

		if (!inputValue) {
			setListItems([]);
			return;
		}

		if (
			(!disableSearchOnMobile || !isMobile) &&
			!(
				!isKeyDownHandled &&
				destinations &&
				inputValue === destinations[0]?.value
			)
		) {
			executeQuery(inputValue);
		}
	}, [inputValue]);

	const getListOffset = () => {
		switch (props.outboundType) {
			case OutboundType.Call:
				return { crossAxis: -380, mainAxis: -34 };
			case OutboundType.Email:
				return { crossAxis: -365, mainAxis: -45 };
			default:
				return { crossAxis: 0, mainAxis: 0 };
		}
	};

	return (
		<Flex
			vAlign="flex-start"
			hAlign="space-between"
			className={mergeClasses(classes.root, classesName?.root)}
		>
			<MultiInput
				disabled={disabled ?? false}
				ref={inputRef}
				label={label}
				items={items}
				limit={limit}
				autoFocus={autoFocus}
				placeholder={placeholder}
				inputValue={inputValue}
				onItemSelected={(selectedItem: string) => {
					//this function is called when pressing the enter key, not from moving the focus away
					selectItemCallback({
						name: "",
						value: selectedItem.trim(),
						contactId: undefined,
						selectionSource: TargetSelectionSource.Manual,
						selectionType: undefined,
					});
				}}
				onInputChanged={setInputValue}
				onInputPaste={onInputPaste}
				onInputBlur={() => {
					//this function is called when moving the focus away, not when pressing the enter key
					!!inputValue &&
						isKeyDownHandled &&
						selectItemCallback({
							name: "",
							value: inputValue.trim(),
							contactId: undefined,
							selectionSource: TargetSelectionSource.Manual,
							selectionType: undefined,
						});
				}}
				onInputFocus={() => {
					setPopoverOpen(hasSuggestions && !!inputValue);
					setListItems((prev) => (!!inputValue ? prev : []));
				}}
				classesName={{
					input: classesName?.input,
					label: classesName?.label,
					contactLabel: classesName?.contactLabel,
					root: classesName?.root,
					container: classesName?.container,
				}}
				isKeyDownHandled={isKeyDownHandled}
				showClearButton={showClearButton}
				onInputClear={onInputClear}
			/>
			{(!disableSearchOnMobile || !isMobile) && (
				<SuggestionsPopover
					target={inputRef.current}
					open={isPopoverOpen}
					items={listItems}
					onDismiss={() => setPopoverOpen(false)}
					positioning={{ offset: getListOffset() }}
					classesName={{
						surface: classes.popoverSurface,
						list: {
							containerHeight: classes.listContainer,
							item: classes.listItem,
							mainText: classes.listMainText,
							startMedia: classes.listItemStartMedia,
						},
					}}
				/>
			)}
			{!!actions && (
				<Flex
					vAlign="center"
					hAlign="flex-end"
					className={classes.actions}
					gap={GapSize.Small}
				>
					{actions?.map((x: ActionItemProps) => (
						<button
							key={x.label}
							className={classes.actionItem}
							children={x.label}
							onClick={x.action}
						/>
					))}
				</Flex>
			)}
		</Flex>
	);
};
